import { useCallback, useEffect, useState } from 'react';

import { BROADCAST_CHANNEL_PREFIX } from '../../common/constants';
import { type RemoteActionsResponse } from '../../common/types';
import { getRemoteActions, getRemoteActionsLegacy } from '../../services/remote-actions';
import { useBroadcastChannel } from '../use-broadcast-channel';

export const useRemoteActionsServiceLegacy = (endpoint: string, product: string) => {
	const [callRemoteActionsService, setCallRemoteActionsService] = useState<boolean>(false);
	const [deactivated, setDeactivated] = useState<boolean>(false);
	const [remoteActionsData, setRemoteActionsData] = useState<RemoteActionsResponse | null>(null);

	useEffect(() => {
		if (callRemoteActionsService && !deactivated) {
			const callAsync = async () => {
				try {
					const response = await getRemoteActionsLegacy(endpoint, product);
					setCallRemoteActionsService(false);
					if (response) {
						setRemoteActionsData(response);
					}
				} catch (error) {
					setDeactivated(true);
				}
			};

			callAsync();
		}
	}, [callRemoteActionsService, endpoint, deactivated, product]);

	return {
		remoteActionsData,
		setCallRemoteActionsService,
		callRemoteActionsService,
		deactivated,
	};
};

type RemoteActionPolledMessage = {
	messageType: 'remote-action-polled';
	response: RemoteActionsResponse;
};

/*
 * Union type of all possible messages that can be sent via broadcast channel.
 */
type Messages = RemoteActionPolledMessage;

export const useRemoteActionsService = (endpoint: string, product: string) => {
	const [callRemoteActionsService, setCallRemoteActionsService] = useState<boolean>(false);
	const [deactivated, setDeactivated] = useState<boolean>(false);
	const [remoteActionsData, setRemoteActionsData] = useState<RemoteActionsResponse | null>(null);
	const onBroadcastUpdate = useCallback(
		(message: MessageEvent<Messages>) => {
			if (message.data.messageType === 'remote-action-polled') {
				setRemoteActionsData(message.data.response);
			}
		},
		[setRemoteActionsData],
	);
	const { postMessage } = useBroadcastChannel<Messages>(
		`${BROADCAST_CHANNEL_PREFIX}-${product}`,
		onBroadcastUpdate,
	);

	useEffect(() => {
		if (callRemoteActionsService && !deactivated) {
			const callAsync = async () => {
				try {
					const response = await getRemoteActions(endpoint, product);
					setCallRemoteActionsService(false);
					if (response) {
						setRemoteActionsData(response);
						postMessage({ response, messageType: 'remote-action-polled' });
					}
				} catch (error) {
					setDeactivated(true);
				}
			};

			callAsync();
		}
	}, [callRemoteActionsService, endpoint, deactivated, product, postMessage]);

	return {
		remoteActionsData,
		setCallRemoteActionsService,
		setDeactivated,
		callRemoteActionsService,
		deactivated,
	};
};
